import { Variable } from '../types'

export interface VariablePanelOptions {
  container: HTMLElement
  onVariableHover?: (name: string) => void
}

export class VariablePanel {
  private container: HTMLElement
  private options: VariablePanelOptions
  private variables: Map<string, Variable> = new Map()
  private loopVariables: Set<string> = new Set(['i', 'j', 'k', 'n', 'count', 'index'])
  
  constructor(options: VariablePanelOptions) {
    this.options = options
    this.container = options.container
    this.setupStyles()
    this.render()
  }
  
  /**
   * 変数を更新
   */
  updateVariables(variables: Map<string, Variable>): void {
    // 変更があった変数を特定
    const changedVariables = new Set<string>()
    
    variables.forEach((variable, name) => {
      const oldVariable = this.variables.get(name)
      if (!oldVariable || oldVariable.value !== variable.value) {
        changedVariables.add(name)
      }
    })
    
    this.variables = new Map(variables)
    this.render(changedVariables)
  }
  
  /**
   * スタイルを設定
   */
  private setupStyles(): void {
    const style = document.createElement('style')
    style.textContent = `
      .variable-panel {
        height: 100%;
        overflow-y: auto;
        overflow-x: hidden;
        padding: 1rem;
        scrollbar-width: thin;
        scrollbar-color: #e5e7eb transparent;
      }
      
      .variable-panel::-webkit-scrollbar {
        width: 6px;
      }
      
      .variable-panel::-webkit-scrollbar-track {
        background: transparent;
      }
      
      .variable-panel::-webkit-scrollbar-thumb {
        background-color: #e5e7eb;
        border-radius: 3px;
      }
      
      .variable-panel::-webkit-scrollbar-thumb:hover {
        background-color: #d1d5db;
      }
      
      .variable-group {
        margin-bottom: 1.5rem;
        flex-shrink: 0;
      }
      
      .variable-group:last-child {
        margin-bottom: 0;
      }
      
      .variable-group__title {
        font-size: 0.875rem;
        font-weight: 600;
        color: var(--text-secondary);
        margin-bottom: 0.5rem;
        display: flex;
        align-items: center;
        gap: 0.5rem;
      }
      
      .variable-group__icon {
        width: 16px;
        height: 16px;
      }
      
      .variable-list {
        display: flex;
        flex-direction: column;
        gap: 0.25rem;
      }
      
      .variable-item {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 0.5rem 0.75rem;
        background-color: var(--surface-color);
        border: 1px solid var(--border-color);
        border-radius: 0.375rem;
        font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
        font-size: 0.875rem;
        transition: all 0.3s ease;
        cursor: default;
        min-height: 40px;
        flex-shrink: 0;
      }
      
      .variable-item:hover {
        background-color: #f3f4f6;
        border-color: var(--primary-color);
      }
      
      .variable-item--loop {
        background-color: rgba(37, 99, 235, 0.05);
        border-color: rgba(37, 99, 235, 0.3);
      }
      
      .variable-item--loop:hover {
        background-color: rgba(37, 99, 235, 0.1);
      }
      
      .variable-item--changed {
        animation: variable-highlight 0.6s ease;
      }
      
      @keyframes variable-highlight {
        0% { 
          background-color: rgba(245, 158, 11, 0.3);
          transform: scale(1.02);
        }
        100% { 
          background-color: var(--surface-color);
          transform: scale(1);
        }
      }
      
      .variable-info {
        display: flex;
        align-items: center;
        gap: 0.75rem;
      }
      
      .variable-name {
        font-weight: 500;
        color: var(--text-primary);
      }
      
      .variable-type {
        font-size: 0.75rem;
        color: var(--text-secondary);
        background-color: #e5e7eb;
        padding: 0.125rem 0.375rem;
        border-radius: 0.25rem;
      }
      
      .variable-value {
        font-weight: 600;
        color: #059669;
      }
      
      .variable-item--loop .variable-value {
        color: var(--primary-color);
      }
      
      .empty-state {
        text-align: center;
        color: var(--text-secondary);
        padding: 2rem;
        font-size: 0.875rem;
        min-height: 200px;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
      }
      
      .empty-state__icon {
        font-size: 2rem;
        margin-bottom: 0.5rem;
        opacity: 0.5;
      }
    `
    document.head.appendChild(style)
  }
  
  /**
   * レンダリング
   */
  private render(changedVariables?: Set<string>): void {
    if (this.variables.size === 0) {
      this.container.innerHTML = `
        <div class="variable-panel">
          <div class="empty-state">
            <div class="empty-state__icon">📊</div>
            <div>変数がまだ宣言されていません</div>
          </div>
        </div>
      `
      return
    }
    
    // 変数をグループ分け
    const loopVars: Array<[string, Variable]> = []
    const normalVars: Array<[string, Variable]> = []
    
    this.variables.forEach((variable, name) => {
      if (this.isLoopVariable(name)) {
        loopVars.push([name, variable])
      } else {
        normalVars.push([name, variable])
      }
    })
    
    let html = '<div class="variable-panel">'
    
    // ループ変数グループ
    if (loopVars.length > 0) {
      html += `
        <div class="variable-group">
          <div class="variable-group__title">
            <span class="variable-group__icon">🔄</span>
            ループ変数
          </div>
          <div class="variable-list">
            ${loopVars.map(([name, variable]) => this.renderVariable(name, variable, true, changedVariables?.has(name))).join('')}
          </div>
        </div>
      `
    }
    
    // 通常変数グループ
    if (normalVars.length > 0) {
      html += `
        <div class="variable-group">
          <div class="variable-group__title">
            <span class="variable-group__icon">📦</span>
            変数
          </div>
          <div class="variable-list">
            ${normalVars.map(([name, variable]) => this.renderVariable(name, variable, false, changedVariables?.has(name))).join('')}
          </div>
        </div>
      `
    }
    
    html += '</div>'
    this.container.innerHTML = html
    
    // イベントリスナーを設定
    this.setupEventListeners()
  }
  
  /**
   * 変数をレンダリング
   */
  private renderVariable(name: string, variable: Variable, isLoop: boolean, isChanged?: boolean): string {
    const classes = [
      'variable-item',
      isLoop ? 'variable-item--loop' : '',
      isChanged ? 'variable-item--changed' : ''
    ].filter(Boolean).join(' ')
    
    return `
      <div class="${classes}" data-variable="${name}">
        <div class="variable-info">
          <span class="variable-name">${name}</span>
          <span class="variable-type">${variable.type}</span>
        </div>
        <span class="variable-value">${this.formatValue(variable.value, variable.type)}</span>
      </div>
    `
  }
  
  /**
   * 値をフォーマット
   */
  private formatValue(value: any, type: string): string {
    switch (type) {
      case 'int':
        return String(Math.floor(value))
      case 'float':
        return Number(value).toFixed(2)
      case 'char':
        return `'${value}'`
      default:
        return String(value)
    }
  }
  
  /**
   * ループ変数かどうかを判定
   */
  private isLoopVariable(name: string): boolean {
    return this.loopVariables.has(name) || 
           name.length === 1 && name >= 'i' && name <= 'n'
  }
  
  /**
   * イベントリスナーを設定
   */
  private setupEventListeners(): void {
    const items = this.container.querySelectorAll('.variable-item')
    items.forEach(item => {
      item.addEventListener('mouseenter', () => {
        const name = item.getAttribute('data-variable')
        if (name && this.options.onVariableHover) {
          this.options.onVariableHover(name)
        }
      })
    })
  }
  
  /**
   * クリーンアップ
   */
  dispose(): void {
    this.container.innerHTML = ''
  }
}